import React, { useState, useEffect } from 'react'
import { useDispatch, useSelector } from 'react-redux';
import { 
  FolderIcon, 
  MagnifyingGlassIcon, 
  PlusIcon,
  PencilIcon,
  TrashIcon,
  StarIcon,
  CurrencyDollarIcon,
  WrenchScrewdriverIcon,
  BeakerIcon
} from '@heroicons/react/24/outline'
import { 
  StarIcon as StarIconSolid,
  CurrencyDollarIcon as CurrencyDollarIconSolid,
  WrenchScrewdriverIcon as WrenchScrewdriverIconSolid,
  BeakerIcon as BeakerIconSolid
} from '@heroicons/react/24/solid'
import toast from 'react-hot-toast'
import { 
  useGetProjectsQuery, 
  useGetProjectStatsQuery, 
  useUpdateProjectMutation,
  useDeleteProjectMutation, 
  useSetDemoProjectMutation, 
  useUnsetDemoProjectMutation,
  useSetTrialProjectMutation,
  useUnsetTrialProjectMutation,
  useSetAccountProjectMutation,
  useUnsetAccountProjectMutation,
  useSetTechSupportProjectMutation,
  useUnsetTechSupportProjectMutation,
  useSetShowMotionodeSalesMutation,
  useUnsetShowMotionodeSalesMutation,
  useSetShowMotionodeSupportMutation,
  useUnsetShowMotionodeSupportMutation,
} from '../store/api/projectsApi'
import { useGetMotionodeSupportSalesUsersQuery } from '../store/api/usersApi'
import { formatDateTimeEST } from '../utils/dateUtils'
import SortableTableHeader from '../components/SortableTableHeader'

interface Project {
  id: number
  name: string
  description?: string
  organization_name: string
  owner_name?: string | null
  owner_email?: string | null
  created_at: string
  members_count: number
  progress: number
  is_demo?: number
  is_trial?: number
  is_account_project?: number
  is_tech_support_project?: number
  show_motionode_sales?: number
  show_motionode_support?: number
  commercial_status?: string | null
  initial_saved_minutes?: number
  update_saved_minutes?: number
}

interface ProjectFilters {
  page: number;
  limit: number;
  search: string;
  sortBy: string;
  sortOrder: 'ASC' | 'DESC';
}

const Projects: React.FC = () => {
  const [filters, setFilters] = useState<ProjectFilters>({
    page: 1,
    limit: 10000,
    search: '',
    sortBy: 'created_at',
    sortOrder: 'DESC'
  });

  const { data: projectsData, isLoading, error } = useGetProjectsQuery(filters, {
    refetchOnMountOrArgChange: true,
    refetchOnFocus: true,
  })

  const { data: statsData, isLoading: statsLoading } = useGetProjectStatsQuery()
  const [updateProject] = useUpdateProjectMutation()
  const [deleteProject] = useDeleteProjectMutation()
  const [setDemoProject] = useSetDemoProjectMutation()
  const [unsetDemoProject] = useUnsetDemoProjectMutation()
  const [setTrialProject] = useSetTrialProjectMutation()
  const [unsetTrialProject] = useUnsetTrialProjectMutation()
  const [editingProject, setEditingProject] = useState<Project | null>(null)
  const [editName, setEditName] = useState('')
  const [setAccountProject] = useSetAccountProjectMutation()
  const [unsetAccountProject] = useUnsetAccountProjectMutation()
  const [setTechSupportProject] = useSetTechSupportProjectMutation()
  const [unsetTechSupportProject] = useUnsetTechSupportProjectMutation()
  const [setShowMotionodeSales] = useSetShowMotionodeSalesMutation()
  const [unsetShowMotionodeSales] = useUnsetShowMotionodeSalesMutation()
  const [setShowMotionodeSupport] = useSetShowMotionodeSupportMutation()
  const [unsetShowMotionodeSupport] = useUnsetShowMotionodeSupportMutation()
  const { data: motionodeStaff } = useGetMotionodeSupportSalesUsersQuery()
  const [deletingProjectId, setDeletingProjectId] = useState<number | null>(null)

  const supportPersonLabel = motionodeStaff?.support?.name || motionodeStaff?.support?.email || 'Support'
  const salesPersonLabel = motionodeStaff?.sales?.name || motionodeStaff?.sales?.email || 'Sales'

  const dispatch = useDispatch();
  // Try to get selectedProject from Redux (adjust selector as needed for your store structure)
  const selectedProject = useSelector((state: any) => state.projects?.selectedProject);

useEffect(() => {
  if (!projectsData?.data?.length) return; // previene undefined o array vacío

  if (!selectedProject) {
    dispatch({
      type: 'projects/setSelectedProject',
      payload: projectsData.data[0],
    });
  }
}, [projectsData, selectedProject, dispatch]);

  // Handle search
  const handleSearch = (value: string) => {
    setFilters(prev => ({ ...prev, search: value, page: 1 }));
  };

  // Handle sorting
  const handleSort = (sortBy: string, sortOrder: 'ASC' | 'DESC') => {
    setFilters(prev => ({ ...prev, sortBy, sortOrder, page: 1 }));
  };

  // Handle pagination
  // const handlePageChange = (page: number) => {
  //   setFilters(prev => ({ ...prev, page }));
  // };

  const handleOpenEdit = (project: Project) => {
    setEditingProject(project)
    setEditName(project.name)
  }
  const handleCloseEdit = () => {
    setEditingProject(null)
    setEditName('')
  }
  const handleSaveEdit = async () => {
    if (!editingProject || !editName.trim()) return
    try {
      await updateProject({ id: editingProject.id, name: editName.trim() }).unwrap()
      toast.success('Project name updated')
      handleCloseEdit()
    } catch (err: any) {
      toast.error(err?.data?.error || err?.message || 'Failed to update project')
    }
  }
  const handleDeleteProject = async (projectId: number) => {
    if (!window.confirm('Are you sure you want to delete this project?')) {
      return;
    }

    try {
      setDeletingProjectId(projectId);
      await deleteProject(projectId).unwrap();
      toast.success('Project deleted successfully');

      if (selectedProject?.id === projectId) {
        dispatch({ type: 'projects/setSelectedProject', payload: null });
      }
    } catch (err) {
      console.error(err);
      toast.error('Failed to delete project');
    } finally {
      setDeletingProjectId(null);
    }
  };

  const handleSetDemo = async (projectId: number, isDemo: boolean) => {
    try {
      if (isDemo) {
        await unsetDemoProject(projectId).unwrap();
        toast.success('Project unset as demo');
      } else {
        await setDemoProject(projectId).unwrap();
        toast.success('Project set as demo successfully');
      }
    } catch (err: any) {
      console.error(err);
      toast.error(err?.data?.message || 'Failed to update demo project');
    }
  };

  const handleSetTrial = async (projectId: number, isTrial: boolean) => {
    try {
      if (isTrial) {
        await unsetTrialProject(projectId).unwrap();
        toast.success('Project unset as trial');
      } else {
        await setTrialProject(projectId).unwrap();
        toast.success('Project set as trial successfully');
      }
    } catch (err: any) {
      console.error(err);
      toast.error(err?.data?.message || 'Failed to update trial project');
    }
  };

  const handleSetAccountProject = async (projectId: number, isAccountProject: boolean) => {
    try {
      if (isAccountProject) {
        await unsetAccountProject(projectId).unwrap();
        toast.success('Project unset as Account project');
      } else {
        await setAccountProject(projectId).unwrap();
        toast.success('Project set as Account project successfully');
      }
    } catch (err: any) {
      console.error(err);
      toast.error(err?.data?.message || 'Failed to update Account project');
    }
  };

  const handleSetTechSupportProject = async (projectId: number, isTechSupportProject: boolean) => {
    try {
      if (isTechSupportProject) {
        await unsetTechSupportProject(projectId).unwrap();
        toast.success('Project unset as Tech Support project');
      } else {
        await setTechSupportProject(projectId).unwrap();
        toast.success('Project set as Tech Support project successfully');
      }
    } catch (err: any) {
      console.error(err);
      toast.error(err?.data?.message || 'Failed to update Tech Support project');
    }
  };

  const handleToggleMotionodeSupport = async (projectId: number, enabled: boolean) => {
    try {
      if (enabled) {
        await unsetShowMotionodeSupport(projectId).unwrap();
        toast.success('Motionode Support hidden for this project');
      } else {
        await setShowMotionodeSupport(projectId).unwrap();
        toast.success(`Motionode Support (${supportPersonLabel}) enabled for this project`);
      }
    } catch (err: any) {
      console.error(err);
      toast.error(err?.data?.message || 'Failed to update Motionode Support visibility');
    }
  };

  const handleToggleMotionodeSales = async (projectId: number, enabled: boolean) => {
    try {
      if (enabled) {
        await unsetShowMotionodeSales(projectId).unwrap();
        toast.success('Motionode Sales hidden for this project');
      } else {
        await setShowMotionodeSales(projectId).unwrap();
        toast.success(`Motionode Sales (${salesPersonLabel}) enabled for this project`);
      }
    } catch (err: any) {
      console.error(err);
      toast.error(err?.data?.message || 'Failed to update Motionode Sales visibility');
    }
  };

  const projects = projectsData?.data || []

  const stats = statsData || {
    totalProjects: 0,
    activeProjects: 0,
    completedProjects: 0,
    suspendedProjects: 0,
    cancelledProjects: 0,
    avgProgress: 0,
    newToday: 0,
    newThisMonth: 0
  }

  if (isLoading) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="animate-spin rounded-full h-32 w-32 border-b-2 border-blue-600"></div>
      </div>
    )
  }

  if (error) {
    return (
      <div className="text-center py-8">
        <p className="text-red-600">Error loading projects. Please try again.</p>
      </div>
    )
  }

  // const getStatusColor = (status: string) => {
  //   switch (status) {
  //     case 'ACTIVE': return 'bg-green-100 text-green-800'
  //     case 'COMPLETED': return 'bg-blue-100 text-blue-800'
  //     case 'SUSPENDED': return 'bg-yellow-100 text-yellow-800'
  //     case 'CANCELLED': return 'bg-red-100 text-red-800'
  //     default: return 'bg-gray-100 text-gray-800'
  //   }
  // }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Projects</h1>
          <p className="text-gray-600">Manage and monitor all projects across organizations</p>
        </div>
        <button className="bg-blue-600 text-white px-4 py-2 rounded-md hover:bg-blue-700 flex items-center gap-2">
          <PlusIcon className="h-5 w-5" />
          New Project
        </button>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
        <div className="bg-white p-6 rounded-lg shadow-sm border border-gray-200">
          <div className="flex items-center">
            <div className="flex-shrink-0">
              <FolderIcon className="h-6 w-6 text-blue-600" />
            </div>
            <div className="ml-4">
              <p className="text-sm text-gray-500">Total Projects</p>
              <p className="text-2xl font-bold text-gray-900">{statsLoading ? '-' : stats.totalProjects}</p>
            </div>
          </div>
        </div>
        
        <div className="bg-white p-6 rounded-lg shadow-sm border border-gray-200">
          <div className="flex items-center">
            <div className="flex-shrink-0">
              <div className="w-6 h-6 bg-green-500 rounded-full"></div>
            </div>
            <div className="ml-4">
              <p className="text-sm text-gray-500">Active Projects</p>
              <p className="text-2xl font-bold text-gray-900">
                {statsLoading ? '-' : stats.activeProjects}
              </p>
            </div>
          </div>
        </div>
        
        <div className="bg-white p-6 rounded-lg shadow-sm border border-gray-200">
          <div className="flex items-center">
            <div className="flex-shrink-0">
              <div className="w-6 h-6 bg-blue-500 rounded-full"></div>
            </div>
            <div className="ml-4">
              <p className="text-sm text-gray-500">Completed</p>
              <p className="text-2xl font-bold text-gray-900">
                {statsLoading ? '-' : stats.completedProjects}
              </p>
            </div>
          </div>
        </div>
        
        <div className="bg-white p-6 rounded-lg shadow-sm border border-gray-200">
          <div className="flex items-center">
            <div className="flex-shrink-0">
              <div className="w-6 h-6 bg-yellow-500 rounded-full"></div>
            </div>
            <div className="ml-4">
              <p className="text-sm text-gray-500">Avg. Progress</p>
              <p className="text-2xl font-bold text-gray-900">
                {statsLoading ? '-' : Math.round(stats.avgProgress)}%
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Filters */}
      <div className="bg-white p-6 rounded-lg shadow-sm border border-gray-200">
        <div className="flex flex-col sm:flex-row gap-4">
          <div className="flex-1">
            <div className="relative">
              <MagnifyingGlassIcon className="absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-gray-400" />
              <input
                type="text"
                placeholder="Search projects..."
                className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                value={filters.search}
                onChange={(e) => handleSearch(e.target.value)}
              />
            </div>
          </div>

        </div>
      </div>

      {/* Projects Table */}
      <div className="bg-white shadow-sm rounded-lg border border-gray-200">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <SortableTableHeader
                  label="Project"
                  sortKey="name"
                  currentSortBy={filters.sortBy}
                  currentSortOrder={filters.sortOrder}
                  onSort={handleSort}
                />
                <SortableTableHeader
                  label="Organization"
                  sortKey="organization_name"
                  currentSortBy={filters.sortBy}
                  currentSortOrder={filters.sortOrder}
                  onSort={handleSort}
                />
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Owner name
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Owner email
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Status
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Commercial
                </th>
                <SortableTableHeader
                  label="Progress"
                  sortKey="progress"
                  currentSortBy={filters.sortBy}
                  currentSortOrder={filters.sortOrder}
                  onSort={handleSort}
                />
                <SortableTableHeader
                  label="Members"
                  sortKey="members_count"
                  currentSortBy={filters.sortBy}
                  currentSortOrder={filters.sortOrder}
                  onSort={handleSort}
                />
                <SortableTableHeader
                  label="Init. Saved"
                  sortKey="initial_saved_minutes"
                  currentSortBy={filters.sortBy}
                  currentSortOrder={filters.sortOrder}
                  onSort={handleSort}
                />
                <SortableTableHeader
                  label="Upd. Saved"
                  sortKey="update_saved_minutes"
                  currentSortBy={filters.sortBy}
                  currentSortOrder={filters.sortOrder}
                  onSort={handleSort}
                />
                <SortableTableHeader
                  label="Created"
                  sortKey="created_at"
                  currentSortBy={filters.sortBy}
                  currentSortOrder={filters.sortOrder}
                  onSort={handleSort}
                />
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Support person
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Sales person
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {projects.map((project: Project) => (
                <tr key={project.id} className="hover:bg-gray-50">
                  <td className="px-6 py-4 max-w-[280px]">
                    <div className="min-w-0">
                      <div className="text-sm font-medium text-gray-900 truncate" title={project.name}>
                        {project.name && (project.name.length > 50 ? `${project.name.slice(0, 50)}…` : project.name)}
                      </div>
                      {project.description && (
                        <div className="text-sm text-gray-500 truncate" title={project.description}>
                          {project.description.length > 50 ? `${project.description.slice(0, 50)}…` : project.description}
                        </div>
                      )}
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                    {project.organization_name ?? '—'}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                    {project.owner_name ?? '—'}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-600">
                    {project.owner_email ?? '—'}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <span className="inline-flex px-2 py-1 text-xs font-semibold rounded-full bg-green-100 text-green-800">
                      ACTIVE
                    </span>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    {(() => {
                      const cs = (project.commercial_status || 'draft').toLowerCase();
                      const styles: Record<string, string> = {
                        draft: 'bg-gray-100 text-gray-700',
                        proposed: 'bg-blue-100 text-blue-700',
                        accepted: 'bg-green-100 text-green-700',
                        rejected: 'bg-red-100 text-red-700',
                      };
                      return (
                        <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full capitalize ${styles[cs] ?? styles.draft}`}>
                          {cs}
                        </span>
                      );
                    })()}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="flex items-center">
                      <div className="w-full bg-gray-200 rounded-full h-2">
                        <div 
                          className="bg-blue-600 h-2 rounded-full" 
                          style={{ width: `${project.progress}%` }}
                        ></div>
                      </div>
                      <span className="ml-2 text-sm text-gray-600">{project.progress}%</span>
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                    {project.members_count} members
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                    {project.initial_saved_minutes
                      ? `${(project.initial_saved_minutes / 60).toFixed(1)}h`
                      : '—'}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                    {project.update_saved_minutes
                      ? `${(project.update_saved_minutes / 60).toFixed(1)}h`
                      : '—'}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                    {formatDateTimeEST(project.created_at)}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <button
                      type="button"
                      onClick={() => handleToggleMotionodeSupport(project.id, project.show_motionode_support === 1)}
                      className={`inline-flex items-center gap-2 px-2.5 py-1 rounded-full text-xs font-medium border transition-colors ${
                        project.show_motionode_support === 1
                          ? 'bg-purple-50 text-purple-800 border-purple-200 hover:bg-purple-100'
                          : 'bg-gray-50 text-gray-500 border-gray-200 hover:bg-gray-100'
                      }`}
                      title={
                        project.show_motionode_support === 1
                          ? `Click to hide ${supportPersonLabel} in Communication Hub`
                          : `Click to show ${supportPersonLabel} in Communication Hub`
                      }
                    >
                      <span
                        className={`inline-block h-2 w-2 rounded-full ${
                          project.show_motionode_support === 1 ? 'bg-purple-500' : 'bg-gray-300'
                        }`}
                      />
                      {project.show_motionode_support === 1 ? supportPersonLabel : 'Off'}
                    </button>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <button
                      type="button"
                      onClick={() => handleToggleMotionodeSales(project.id, project.show_motionode_sales === 1)}
                      className={`inline-flex items-center gap-2 px-2.5 py-1 rounded-full text-xs font-medium border transition-colors ${
                        project.show_motionode_sales === 1
                          ? 'bg-blue-50 text-blue-800 border-blue-200 hover:bg-blue-100'
                          : 'bg-gray-50 text-gray-500 border-gray-200 hover:bg-gray-100'
                      }`}
                      title={
                        project.show_motionode_sales === 1
                          ? `Click to hide ${salesPersonLabel} in Communication Hub`
                          : `Click to show ${salesPersonLabel} in Communication Hub`
                      }
                    >
                      <span
                        className={`inline-block h-2 w-2 rounded-full ${
                          project.show_motionode_sales === 1 ? 'bg-blue-500' : 'bg-gray-300'
                        }`}
                      />
                      {project.show_motionode_sales === 1 ? salesPersonLabel : 'Off'}
                    </button>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                    <div className="flex items-center gap-2">
                      {/* Demo Project - Star Icon */}
                      <button 
                        onClick={() => handleSetDemo(project.id, project.is_demo === 1)}
                        className={`${project.is_demo === 1 ? 'text-yellow-600 hover:text-yellow-800' : 'text-gray-400 hover:text-yellow-600'}`}
                        title={project.is_demo === 1 ? 'Unset as Demo' : 'Set as Demo Project'}
                      >
                        {project.is_demo === 1 ? (
                          <StarIconSolid className="h-4 w-4" />
                        ) : (
                          <StarIcon className="h-4 w-4" />
                        )}
                      </button>
                      {/* Trial Project - Beaker Icon */}
                      <button
                        onClick={() => handleSetTrial(project.id, project.is_trial === 1)}
                        className={`${project.is_trial === 1 ? 'text-teal-600 hover:text-teal-800' : 'text-gray-400 hover:text-teal-600'}`}
                        title={project.is_trial === 1 ? 'Unset as Trial' : 'Set as Trial Project'}
                      >
                        {project.is_trial === 1 ? (
                          <BeakerIconSolid className="h-4 w-4" />
                        ) : (
                          <BeakerIcon className="h-4 w-4" />
                        )}
                      </button>
                      {/* Account Project - Dollar Icon */}
                      <button 
                        onClick={() => handleSetAccountProject(project.id, project.is_account_project === 1)}
                        className={`${project.is_account_project === 1 ? 'text-green-600 hover:text-green-800' : 'text-gray-400 hover:text-green-600'}`}
                        title={project.is_account_project === 1 ? 'Unset as Account Project' : 'Set as Account Project'}
                      >
                        {project.is_account_project === 1 ? (
                          <CurrencyDollarIconSolid className="h-4 w-4" />
                        ) : (
                          <CurrencyDollarIcon className="h-4 w-4" />
                        )}
                      </button>
                      {/* Tech Support Project - Wrench Icon */}
                      <button 
                        onClick={() => handleSetTechSupportProject(project.id, project.is_tech_support_project === 1)}
                        className={`${project.is_tech_support_project === 1 ? 'text-purple-600 hover:text-purple-800' : 'text-gray-400 hover:text-purple-600'}`}
                        title={project.is_tech_support_project === 1 ? 'Unset as Tech Support Project' : 'Set as Tech Support Project'}
                      >
                        {project.is_tech_support_project === 1 ? (
                          <WrenchScrewdriverIconSolid className="h-4 w-4" />
                        ) : (
                          <WrenchScrewdriverIcon className="h-4 w-4" />
                        )}
                      </button>
                      <button
                        onClick={() => handleOpenEdit(project)}
                        className="text-green-600 hover:text-green-800"
                        title="Edit project name"
                      >
                        <PencilIcon className="h-4 w-4" />
                      </button>
                      <button
                        onClick={() => handleDeleteProject(project.id)}
                        disabled={deletingProjectId === project.id}
                        className="text-red-600 hover:text-red-800 disabled:opacity-50 disabled:cursor-not-allowed"
                        title="Delete project"
                      >
                        <TrashIcon className="h-4 w-4" />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
      </div>

      {/* Edit project name modal */}
      {editingProject && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50" onClick={handleCloseEdit}>
          <div className="bg-white rounded-lg shadow-xl p-6 max-w-md w-full mx-4" onClick={e => e.stopPropagation()}>
            <h3 className="text-lg font-semibold text-gray-900 mb-4">Edit project name</h3>
            <input
              type="text"
              value={editName}
              onChange={e => setEditName(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              placeholder="Project name"
              autoFocus
            />
            <div className="mt-4 flex justify-end gap-2">
              <button
                type="button"
                onClick={handleCloseEdit}
                className="px-4 py-2 text-gray-700 bg-gray-100 rounded-md hover:bg-gray-200"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={handleSaveEdit}
                disabled={!editName.trim()}
                className="px-4 py-2 text-white bg-blue-600 rounded-md hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                Save
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}

export default Projects 